import { Popover, PopoverContent, PopoverTrigger, useDisclosure } from '@chakra-ui/react';
import cn from 'classnames';
import { Emoji } from 'emoji-mart';
import { groupBy } from 'lodash';
import { useRouter } from 'next/router';
import * as React from 'react';

import { getFileType } from '..';
import { fileIconList } from '../assets/data';
import { useFileStore } from '../store/useFileStore';
import { EmbedFaviconWithTitle } from './EmbedFavicon';

const { useCallback, useEffect, useState } = React;

export const PublicFileViewNavbar = () => {
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;

  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );
  const internalFileGroups = groupBy(currentFile?.internalFiles, 'view');

  const [isPopOpen, setIsPopOpen] = useState(false);

  const {
    isOpen: isFileSubMenuOpen,
    onToggle: onSubMenuToggle,
    onClose: onFileSubMenuClose,
  } = useDisclosure();

  useEffect(() => {
    if (!isPopOpen) {
      isDocSubMenuOpen && onDocSubMenuToggle();
      isFileSubMenuOpen && onSubMenuToggle();
      isWhiteboardSubMenuOpen && onWhiteboardSubMenuToggle();
    }
  }, [isPopOpen]);

  const {
    isOpen: isDocSubMenuOpen,
    onToggle: onDocSubMenuToggle,
    onClose: onDocSubMenuClose,
  } = useDisclosure();

  const {
    isOpen: isListSubMenuOpen,
    onToggle: onListSubMenuToggle,
    onClose: onListSubMenuClose,
  } = useDisclosure();

  const {
    isOpen: isWhiteboardSubMenuOpen,
    onToggle: onWhiteboardSubMenuToggle,
    onClose: onWhiteboardSubMenuClose,
  } = useDisclosure();

  return (
    <div
      className={cn({
        'fixed top-63': router.query.type === 'whiteboard',
        'w-full border-b border-t border-gray-200 z-50 bg-white px-24 flex py-4': true,
      })}
    >
      <button
        className="flex items-center pr-16"
        onClick={() => {
          router.push(
            `/${mode}/${orgId}/files/${router.query.slug}/public?view=${
              currentFile?.primaryFile?.id
            }&type=${
              currentFile?.primaryFile?.type === 'board'
                ? currentFile?.primaryFile?.view
                : currentFile?.primaryFile?.type
            }`
          );
        }}
      >
        {currentFile?.icon ? (
          <span className="mt-3">
            <Emoji emoji={currentFile?.icon} set="apple" size={15} />
          </span>
        ) : (
          <span className="mt-3">
            <Emoji emoji="bulb" set="apple" size={15} />
          </span>
        )}
        <span className={`max-w-300 truncate ml-2 text-sm`}>
          {currentFile?.name && currentFile?.name.length > 0 ? currentFile?.name : 'Untitled'}
        </span>
      </button>
      <div className="flex my-auto overflow-scroll no-scrollbar">
        {Object.keys(internalFileGroups).map((filetype) => {
          const [firstItem, ...restItems] = internalFileGroups[filetype];
          if (restItems.length > 0) {
            const { id, type, view } = firstItem;
            return type === 'embed' ? (
              <div className="flex">
                <span className="mx-12">|</span>
                <ViewMenu
                  isSubMenuOpen={isFileSubMenuOpen}
                  id={id}
                  type={type}
                  view={view}
                  onClick={onSubMenuToggle}
                  onClose={onFileSubMenuClose}
                  setIsPopOpen={setIsPopOpen}
                />
              </div>
            ) : type === 'document' ? (
              <div className="flex">
                <span className="mx-12">|</span>
                <ViewMenu
                  isSubMenuOpen={isDocSubMenuOpen}
                  id={id}
                  type={type}
                  view={view}
                  onClick={onDocSubMenuToggle}
                  onClose={onDocSubMenuClose}
                  setIsPopOpen={setIsPopOpen}
                />
              </div>
            ) : type === 'list' ? (
              <ViewMenu
                isSubMenuOpen={isListSubMenuOpen}
                id={id}
                type={type}
                view={view}
                onClick={onListSubMenuToggle}
                onClose={onListSubMenuClose}
                setIsPopOpen={setIsPopOpen}
              />
            ) : (
              <div className="flex">
                <span className="mx-12">|</span>
                <ViewMenu
                  isSubMenuOpen={isWhiteboardSubMenuOpen}
                  id={id}
                  type={type}
                  view={view}
                  onClick={onWhiteboardSubMenuToggle}
                  onClose={onWhiteboardSubMenuClose}
                  setIsPopOpen={setIsPopOpen}
                />
              </div>
            );
          } else {
            const { id, type, view, name, data, viewId } = firstItem;
            return (
              <div className="flex">
                <span className="mx-12 block text-gray-500">|</span>
                <button
                  className="flex relative"
                  onClick={() => {
                    router.push({
                      pathname: `/${mode}/${orgId}/files/${router.query.slug}/public`,
                      query: {
                        view: id,
                        type: getFileType({
                          type: type,
                          view: view,
                        }),
                        viewId: viewId,
                      },
                    });
                  }}
                >
                  <div
                    key={id}
                    className={`font-medium mt-1 flex items-center text-sm relative ${
                      router.query.view === id && router.query.type === view
                        ? 'text-blue-500 '
                        : 'text-gray-500'
                    } `}
                  >
                    {type !== 'embed' ? (
                      <>
                        <span className="block mt-2 mr-4">
                          {fileIconList[`${type === 'board' ? view : type}`]}
                        </span>
                        {name && name.length > 0 ? (
                          <span className="mr-6 truncate max-w-80 w-70 text-left">{name}</span>
                        ) : (
                          <span className="capitalize mr-6">{type === 'board' ? view : type}</span>
                        )}
                      </>
                    ) : (
                      <EmbedFaviconWithTitle url={data?.url} name={name} />
                    )}
                  </div>
                </button>
              </div>
            );
          }
        })}
      </div>
    </div>
  );
};

const ViewMenu = ({
  isSubMenuOpen,
  id,
  type,
  view,
  onClose,
  onClick,
}: {
  isSubMenuOpen: boolean;
  id: string;
  type: string;
  view: string;
  onClose: () => void;
  onClick: () => void;
  setIsPopOpen: (boolean) => void;
}) => {
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;

  const updateURL = (type, view, viewId) => {
    router.push({
      pathname: `/${mode}/${orgId}/files/${router.query.slug}/public`,
      query: { type: type, view: view, viewId: viewId },
    });
  };

  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );
  const internalFileGroups = groupBy(currentFile?.internalFiles, 'view');
  const viewType = getFileType({ type, view });
  const isSelected = internalFileGroups[viewType].find(
    (view) => view.viewId === router.query.viewId
  );

  return (
    <div>
      <Popover isOpen={isSubMenuOpen} onClose={onClose}>
        <PopoverTrigger>
          <button className="flex relative" onClick={onClick}>
            <div
              key={`primary-${id}`}
              className={`font-medium mt-1 flex items-center text-sm relative ${
                isSelected ? 'text-blue-500 ' : 'text-gray-500'
              } `}
            >
              <span className="block mt-2 mr-4">{fileIconList[viewType]}</span>
              <span className="capitalize mr-6">
                {isSelected ? isSelected.name || viewType : viewType}
              </span>
              <span className="bg-primary-100 text-primary-600 rounded-full w-20 h-20 text-xs font-thin flex justify-center align-middle items-center">
                {internalFileGroups[viewType] && internalFileGroups[viewType].length}
              </span>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent>
          <ul className="bg-white p-2 rounded-sm shadow-sm flex flex-col list-none">
            {internalFileGroups[viewType] &&
              internalFileGroups[viewType].map(({ id, view, name, type, data, viewId }) => (
                <li key={id} className="flex bg-white hover:bg-gray-100 items-center pr-8">
                  <button
                    className="flex relative px-12 py-6 items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose();
                      updateURL(viewType, id, viewId);
                    }}
                    // ref={embedBtn}
                  >
                    <div
                      key={id}
                      className={`font-medium mt-1 flex items-center text-sm relative ${
                        router.query.view === id &&
                        router.query.type === view &&
                        router.query.viewId === viewId
                          ? 'text-blue-500 '
                          : 'text-gray-500'
                      } `}
                    >
                      {type !== 'embed' ? (
                        <>
                          <span className="block mt-2 mr-6">
                            {fileIconList[`${type === 'board' ? view : type}`]}
                          </span>
                          <span className="capitalize mr-6 truncate max-w-80 w-80 text-left">
                            {name && name.length > 0 ? name : type === 'board' ? view : type}
                          </span>
                        </>
                      ) : (
                        <EmbedFaviconWithTitle url={data?.url} name={name} />
                      )}
                    </div>
                  </button>
                </li>
              ))}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
};
